import { WaveformParams } from './types';

/**
 * Нормализует значения waveform, приводя исходный диапазон к целевому,
 * сохраняя пропорции громкости между значениями.
 *
 * @param rawValues - Массив сырых значений waveform.
 * @param minValidRawValuesMaximum - Минимально допустимый максимум
 * исходного waveform, используемый при нормализации. Если фактический максимум waveform меньше этого
 * значения, используется `minValidRawValuesMaximum`. Это предотвращает ситуацию, когда слишком тихий waveform
 * растягивается на весь целевой диапазон и выяглдит так как будно в ГС перманентно громкий звук, а не тишина.
 * @param targetDiapason - Целевой диапазон значений.
 *
 * @example
 * // Обычная нормализация растянется на всю высоту контейнера
 * normalizeWaveformValues([0.1, 0.5, 1], 0.05, { min: 1, max: 22 }) => [2, 11, 22]
 * normalizeWaveformValues( [12, 6, 22], 22, { min: 3, max: 40 } )  => [22, 11, 40]
 * // Слишком тихий waveform будет транформироваться относительно полученного максимума
 * normalizeWaveformValues([0.01, 0.02, 0.03], 0.05, { min: 1, max: 22 }) => [4, 9, 13]
 * normalizeWaveformValues([1, 2, 1, 2, 1, 3], 22, { min: 1, max: 22 }) => [2, 4, 2, 4, 2, 5]
 */
const normalizeWaveformValues = (
  rawValues: number[],
  minValidRawValuesMaximum: number,
  targetDiapason: WaveformParams['values'],
) => {
  const maxValue = Math.max(...rawValues, minValidRawValuesMaximum);
  const { min, max } = targetDiapason;

  return rawValues.map((item) => Math.round(Math.max(min, (item / maxValue) * max)));
};

/**
 * bucket-based resampling сжимает аудиомассив до n значений, сохраняя пики сигнала.
 * Делит исходный массив на n равных временных сегментов.
 * В каждом сегменте берёт RMS амплитуду.
 */
const collapseWaveformLength = (waveform: number[], n: number) => {
  const resultWaveform: number[] = new Array(n).fill(0);

  for (let i = 0; i < n; i++) {
    const start = Math.floor((i * waveform.length) / n);
    const end = Math.floor(((i + 1) * waveform.length) / n);

    let sum = 0;

    for (let j = start; j < end; j++) {
      const v = waveform[j];
      sum += v * v;
    }

    const mean = sum / Math.max(1, end - start);
    resultWaveform[i] = Math.sqrt(mean);
  }

  return resultWaveform;
};

/**
 * Линейная интерполяция, увеличивает массив до n значений, сохраняя плавную форму
 * Добавляет промежуточные значения между существующими точками массива.
 */
function expandWaveformLength(waveform: number[], n: number) {
  const result: number[] = new Array(n).fill(0);

  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);

    const scaled = t * (waveform.length - 1);
    const idx = Math.floor(scaled);
    const frac = scaled - idx;

    if (idx >= waveform.length - 1) {
      result[i] = waveform[waveform.length - 1];
    } else {
      result[i] = waveform[idx] + (waveform[idx + 1] - waveform[idx]) * frac;
    }
  }

  return result;
}

const normalizeWaveformLength = (waveform: number[], params: WaveformParams['length']) => {
  const { min, max } = params;

  if (waveform.length > max) {
    return collapseWaveformLength(waveform, max);
  }

  if (waveform.length < min) {
    return expandWaveformLength(waveform, min);
  }

  return waveform;
};

/**
 * Трансформирует текущие значения и размер волны к заданным параметрам
 */
export const normalizeWaveform = (
  rawValues: number[],
  minValidRawValuesMaximum: number,
  targetDiapasonParams: WaveformParams,
) => {
  if (rawValues.length === 0) {
    return Array(targetDiapasonParams.length.min).fill(targetDiapasonParams.values.min);
  }

  return normalizeWaveformValues(
    normalizeWaveformLength(rawValues, targetDiapasonParams.length),
    minValidRawValuesMaximum,
    targetDiapasonParams.values,
  );
};
